import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Chip,
  Divider,
  Paper
} from '@mui/material';

const statusColor = (status) => {
  if (status === 'done' || status === 'ok') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'skipped' || status === 'skip') return 'default';
  return 'warning';
};

/**
 * Shows the post-destroy (or failed destroy) step report so operators can verify cleanup.
 */
const TenantDestroyReportDialog = ({ open, onClose, report, title }) => {
  if (!report) return null;

  const infra = report.infrastructure || report;
  const steps = infra.steps || [];
  const errors = infra.errors || report.errors || [];
  const backupPath = report.backup_file_path;
  const dbDrop = report.database_drop;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title || 'Destroy report'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
          {errors.length > 0 ? (
            <Alert severity="error">
              Infrastructure destroy incomplete. Review steps below — Kubernetes resources may still exist.
            </Alert>
          ) : (
            <Alert severity="success">
              Destroy finished. Use this report to confirm each cleanup step.
            </Alert>
          )}

          {backupPath && (
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              Backup: {backupPath}
            </Typography>
          )}

          {infra.k8s_namespace && (
            <Typography variant="body2">
              Namespace: <strong>{infra.k8s_namespace}</strong>
              {infra.k8s_mode ? ` · mode=${infra.k8s_mode}` : ''}
              {infra.k8s_deleted != null ? ` · deleted=${String(infra.k8s_deleted)}` : ''}
            </Typography>
          )}

          {Array.isArray(infra.k8s_prefixes) && infra.k8s_prefixes.length > 0 && (
            <Typography variant="body2">
              Prefixes cleaned: {infra.k8s_prefixes.join(', ')}
            </Typography>
          )}

          <Divider />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Steps</Typography>
          {steps.length === 0 && (
            <Typography variant="body2" color="text.secondary">No step details returned.</Typography>
          )}
          {steps.map((s) => (
            <Paper key={`${s.step}-${s.key}`} variant="outlined" sx={{ p: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Step ${s.step}`} />
                <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{s.title}</Typography>
                <Chip size="small" color={statusColor(s.status)} label={s.status || 'unknown'} />
              </Box>
              {s.detail && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                  {s.detail}
                </Typography>
              )}
            </Paper>
          ))}

          {dbDrop && (
            <>
              <Divider />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Database drop</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                {typeof dbDrop === 'string' ? dbDrop : JSON.stringify(dbDrop, null, 2)}
              </Typography>
            </>
          )}

          {errors.length > 0 && (
            <>
              <Divider />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.main' }}>Errors</Typography>
              {errors.map((e) => (
                <Typography key={e} variant="body2" color="error">{e}</Typography>
              ))}
            </>
          )}

          {report.mode === 'soft_delete_with_backup' && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Tenant is under Tenants → status filter “Destroyed (recoverable)” for rollback / tracking.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantDestroyReportDialog;
